import React from 'react'
import { Box, Divider, Typography } from '@material-ui/core'
import WarningIcon from '@material-ui/icons/Warning'
import { useStyles } from './styles'
import { UnpaidChargeCard } from './UnpaidChargeCard'
import { MoveOutDetail } from '../../hooks/useMoveOutManagement/useMoveOutManagement'
import { formatCurrency } from './utils'

interface FinancialDispositionProps {
    detail: MoveOutDetail
}

export const FinancialDisposition: React.FC<FinancialDispositionProps> = ({
    detail,
}) => {
    const classes = useStyles()

    const deposit = parseFloat(detail.deposit ?? '0')
    const totalDamages = parseFloat(detail.total_damages ?? '0')
    const pastDueBalance = parseFloat(detail.past_due_balance ?? '0')
    const unpaidCharges = detail.unpaid_charges ?? []
    const unpaidChargesTotal = unpaidCharges.reduce(
        (sum, charge) => sum + parseFloat(charge.amount || '0'),
        0,
    )
    const totalDeductions = totalDamages + pastDueBalance
    const netAmount = deposit - totalDeductions

    return (
        <>
            {/* Past Due Balance */}
            <Box className={classes.damageSection}>
                <Box
                    className={classes.damageHeader}
                    style={{ backgroundColor: 'rgba(245, 158, 11, 0.08)' }}
                >
                    <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                        <WarningIcon
                            style={{ fontSize: 18, color: '#f59e0b' }}
                        />
                        <Typography
                            variant="body2"
                            style={{ fontWeight: 600, color: '#92400e' }}
                        >
                            Past Due Balance ({unpaidCharges.length})
                        </Typography>
                    </Box>
                </Box>
                {unpaidCharges.map((charge, idx) => (
                    <UnpaidChargeCard key={idx} charge={charge} />
                ))}
                <Box className={classes.damageTotal}>
                    <Typography variant="body2" style={{ fontWeight: 600 }}>
                        Total Past Due
                    </Typography>
                    <Typography
                        variant="body2"
                        style={{ fontWeight: 700, color: '#ef4444' }}
                    >
                        {formatCurrency(unpaidChargesTotal)}
                    </Typography>
                </Box>
            </Box>

            <Divider />

            {/* Security Deposit Disposition */}
            <Box className={classes.dispositionSection}>
                <Box className={classes.dispositionHeader}>
                    <Typography variant="body2" style={{ fontWeight: 600 }}>
                        Security Deposit Disposition
                    </Typography>
                </Box>
                <Box className={classes.dispositionBody}>
                    <Box className={classes.dispositionRow}>
                        <Typography variant="body2">
                            Security Deposit Held
                        </Typography>
                        <Typography variant="body2" style={{ fontWeight: 600 }}>
                            {formatCurrency(deposit)}
                        </Typography>
                    </Box>

                    <Box mt={2} mb={1}>
                        <Typography
                            variant="caption"
                            color="textSecondary"
                            style={{
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em',
                                fontWeight: 600,
                            }}
                        >
                            Deductions
                        </Typography>
                    </Box>
                    <Box className={classes.dispositionRow}>
                        <Typography variant="body2" color="textSecondary">
                            Damages
                        </Typography>
                        <Typography
                            variant="body2"
                            style={{ color: '#ef4444' }}
                        >
                            - {formatCurrency(totalDamages)}
                        </Typography>
                    </Box>
                    <Box className={classes.dispositionRow}>
                        <Typography variant="body2" color="textSecondary">
                            Past Due Balance
                        </Typography>
                        <Typography
                            variant="body2"
                            style={{ color: '#ef4444' }}
                        >
                            - {formatCurrency(pastDueBalance)}
                        </Typography>
                    </Box>
                    <Box className={classes.dispositionSubtotal}>
                        <Typography variant="body2" style={{ fontWeight: 600 }}>
                            Total Deductions
                        </Typography>
                        <Typography
                            variant="body2"
                            style={{ fontWeight: 600, color: '#ef4444' }}
                        >
                            - {formatCurrency(totalDeductions)}
                        </Typography>
                    </Box>
                </Box>

                {/* Net Amount */}
                <Box
                    className={classes.finalAmountBox}
                    style={{
                        backgroundColor:
                            netAmount >= 0
                                ? 'rgba(16, 185, 129, 0.08)'
                                : 'rgba(239, 68, 68, 0.08)',
                    }}
                >
                    <Box>
                        <Typography
                            variant="subtitle1"
                            style={{ fontWeight: 600 }}
                        >
                            {netAmount >= 0
                                ? 'Refund Due to Tenant'
                                : 'Amount Owed by Tenant'}
                        </Typography>
                    </Box>
                    <Typography
                        variant="h4"
                        style={{
                            fontWeight: 700,
                            color: netAmount >= 0 ? '#10b981' : '#ef4444',
                        }}
                    >
                        {formatCurrency(Math.abs(netAmount))}
                    </Typography>
                </Box>
            </Box>
        </>
    )
}
